<div class="bk-page md">
  <a class="md__crumb" routerLink="/organization/messages">
    <span class="bk-sym" aria-hidden="true">arrow_back</span>
    Messages
  </a>

  @if (loadState === "error") {
    <!-- The message itself could not be read -->
    <section class="bk-panel md__error" role="alert">
      <span class="bk-sym md__error-icon" aria-hidden="true">sms_failed</span>
      <h1 class="bk-heading md__error-title">This message could not be loaded</h1>
      <p class="md__error-body">
        It may have been deleted, or the link may be incomplete. Check your
        connection and try again.
      </p>
      <div class="md__error-actions">
        <button type="button" class="bk-btn bk-btn--ghost" (click)="handleEdit()">Back to messages</button>
        <button type="button" class="bk-btn bk-btn--primary" (click)="retry()">Try again</button>
      </div>
    </section>
  } @else {
    <!-- Subject, status and what can be done with this message -->
    <header class="bk-page__head">
      <div class="bk-page__intro">
        <p class="bk-eyebrow">{{ eyebrow }}</p>
        @if (loadState === "loading") {
          <div class="md__title-skel" aria-hidden="true">
            <span class="bk-skel" style="width: min(440px, 80%); height: 32px"></span>
            <span class="bk-skel" style="width: min(320px, 60%); height: 14px"></span>
          </div>
        } @else {
          <h1 class="bk-page__title">{{ subject }}</h1>
          <p class="md__meta">
            @if (status) {
              <app-status [text]="status" [status]="status" />
            }
            @if (isArchived) {
              <span>Archived</span>
            }
            @if (from) {
              <span>from {{ from }}</span>
            }
            @if (moment) {
              <span [attr.title]="momentExact || null">
                {{ momentLabel }} {{ momentText }}
                @if (countdown) {
                  <span class="md__countdown">· {{ countdown }}</span>
                }
              </span>
            }
          </p>
        }
      </div>

      <div class="bk-page__actions md__actions">
        @if (canCancel) {
          <ng-container
            *hasPermission="{ category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'cancel-scheduled-messages' }"
          >
            <button type="button" class="bk-btn bk-btn--ghost" (click)="openCancelPrompt()">
              <span class="bk-sym" aria-hidden="true">cancel_schedule_send</span>
              Cancel send
            </button>
          </ng-container>
        }
        @if (isSent && failedCount > 0) {
          <ng-container *hasPermission="{ category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'compose-sms' }">
            <button
              type="button"
              class="bk-btn bk-btn--ghost"
              [attr.aria-disabled]="resendBusy || null"
              [attr.aria-busy]="resendBusy || null"
              matTooltip="Opens a new message with the failed numbers filled in. Nothing is sent until you send it."
              (click)="resendToFailed()"
            >
              <span class="bk-sym" aria-hidden="true">replay</span>
              {{ resendBusy ? "Collecting numbers…" : "Resend to failed" }}
            </button>
          </ng-container>
        }
        @if (isCompleted) {
          <button type="button" class="bk-btn bk-btn--primary" (click)="openReport()">
            <span class="bk-sym" aria-hidden="true">monitoring</span>
            View report
          </button>
        }

        <app-dropdown right="0">
          <button
            type="button"
            class="bk-icon-btn md__more"
            aria-label="Message actions"
            aria-haspopup="menu"
            [attr.aria-expanded]="dropdownComponent?.openOptions ?? false"
            matTooltip="More actions"
            ngProjectAs="clickable-item"
          >
            <span class="bk-sym" aria-hidden="true">more_horiz</span>
          </button>
          <div class="bk-menu__list" ngProjectAs="dropdown-item">
            <button type="button" role="menuitem" class="bk-menu__item" (click)="handleTabMenu('View recipients')">
              <span class="bk-sym" aria-hidden="true">group</span>
              View recipients
            </button>
            <button type="button" role="menuitem" class="bk-menu__item" (click)="handleTabMenu('Duplicate message')">
              <span class="bk-sym" aria-hidden="true">content_copy</span>
              Duplicate message
            </button>
            @if (isSent) {
              <ng-container
                *hasPermission="{ category: 'REPORTS_AND_ANALYTICS', permission: 'export-reports-pdf-excel' }"
              >
                <button
                  type="button"
                  role="menuitem"
                  class="bk-menu__item"
                  [attr.aria-disabled]="exportBusy ? 'true' : null"
                  (click)="exportReceipts('recipients')"
                >
                  <span class="bk-sym" aria-hidden="true">download</span>
                  Download recipients (CSV)
                </button>
              </ng-container>
            }
            @if (isCompleted) {
              <!-- "View report" is the primary header button for completed sends, so it is not repeated here. -->
              <div class="bk-menu__sep" role="separator"></div>
              @if (!isArchived) {
                <button
                  type="button"
                  role="menuitem"
                  class="bk-menu__item"
                  [attr.aria-disabled]="archiving ? 'true' : null"
                  (click)="handleTabMenu('Archive message')"
                >
                  <span class="bk-sym" aria-hidden="true">archive</span>
                  {{ archiving ? "Archiving…" : "Archive message" }}
                </button>
              } @else {
                <button
                  type="button"
                  role="menuitem"
                  class="bk-menu__item"
                  [attr.aria-disabled]="archiving ? 'true' : null"
                  (click)="handleTabMenu('Unarchive message')"
                >
                  <span class="bk-sym" aria-hidden="true">unarchive</span>
                  {{ archiving ? "Restoring…" : "Unarchive message" }}
                </button>
              }
            }
          </div>
        </app-dropdown>
      </div>
    </header>

    <!-- Progress and errors from the actions above -->
    <div class="md__notes" aria-live="polite">
      @if (actionError) {
        <p class="md__notice is-error">
          <span class="bk-sym" aria-hidden="true">error</span>
          {{ actionError }}
        </p>
      }
      @if (resendError) {
        <p class="md__notice is-error">
          <span class="bk-sym" aria-hidden="true">error</span>
          {{ resendError }}
        </p>
      }
      @if (exportError) {
        <p class="md__notice is-error">
          <span class="bk-sym" aria-hidden="true">error</span>
          {{ exportError }}
        </p>
      }
      @if (exportNote) {
        <p class="md__notice">
          <span class="bk-sym" aria-hidden="true">{{ exportBusy ? "hourglass_top" : "task_alt" }}</span>
          @if (exportBusy) {
            <!-- The row count changes every page: announce the start only, then the result. -->
            <span aria-hidden="true">{{ exportNote }}</span>
            <span class="bk-sr-only">Preparing the file…</span>
          } @else {
            {{ exportNote }}
          }
        </p>
      }
    </div>

    <div class="md__grid">
      <div class="md__col">
        <!-- The message, as a phone shows it -->
        <section class="bk-panel md__card" style="--i: 0" aria-labelledby="md-message">
          <header class="md__card-head">
            <h2 class="bk-eyebrow" id="md-message">Message</h2>
          </header>
          @if (loadState === "loading") {
            <div class="md__skel-stack" aria-hidden="true">
              <span class="bk-skel md__skel-bubble"></span>
              <span class="bk-skel" style="width: 160px; height: 6px"></span>
            </div>
          } @else {
            <bk-sms-bubble [text]="content" variant="small" [clamp]="0" />
          }
        </section>

        <!-- Did it get through? -->
        <section class="bk-panel md__card" style="--i: 1" aria-labelledby="md-delivery">
          <header class="md__card-head">
            <h2 class="bk-eyebrow" id="md-delivery">Delivery</h2>
          </header>

          @if (loadState === "loading") {
            <div class="md__skel-stack" aria-hidden="true">
              <span class="bk-skel" style="width: 120px; height: 30px"></span>
              <span class="bk-skel" style="width: 100%; height: 6px"></span>
            </div>
          } @else {
            <message-delivery-summary
              [summary]="summary"
              [state]="summaryState"
              [status]="status"
              [sentAt]="moment"
              [blacklisted]="blacklistedCount"
              mode="link"
              (pick)="openReport($event)"
            >
              <p class="md__note">{{ idleNote }}</p>
            </message-delivery-summary>
          }

          @if (failedCount > 0) {
            <div class="md__failed">
              <div class="md__failed-head">
                <h3 class="md__subhead">Failed recipients</h3>
                <button type="button" class="bk-more" (click)="openReport('failed')">
                  See all {{ formatCount(failedCount) }} <span class="bk-sym" aria-hidden="true">arrow_outward</span>
                </button>
              </div>

              @if (failedPreviewState === "loading") {
                <div class="md__skel-stack" aria-hidden="true">
                  <span class="bk-skel" style="width: 100%; height: 14px"></span>
                  <span class="bk-skel" style="width: 100%; height: 14px"></span>
                  <span class="bk-skel" style="width: 70%; height: 14px"></span>
                </div>
              } @else if (failedPreview.length) {
                <ul class="md__failed-list">
                  @for (row of failedPreview; track row.key) {
                    <li class="md__failed-row">
                      <span class="md__who">
                        <span class="md__who-name">{{ row.name || row.number || "Unknown recipient" }}</span>
                        @if (row.name && row.number) {
                          <span class="md__who-number">{{ row.number }}</span>
                        }
                      </span>
                      <app-status [text]="row.statusLabel" [status]="row.statusTone" />
                    </li>
                  }
                </ul>
              } @else if (failedPreviewState === "error") {
                <p class="md__note">The failed recipients could not be listed here. The full report has them.</p>
              }

              <ng-container
                *hasPermission="{ category: 'REPORTS_AND_ANALYTICS', permission: 'export-reports-pdf-excel' }"
              >
                <div class="md__failed-actions">
                  <button
                    type="button"
                    class="bk-btn bk-btn--ghost"
                    [attr.aria-disabled]="!!exportBusy || null"
                    [attr.aria-busy]="exportBusy === 'failed' || null"
                    (click)="exportReceipts('failed')"
                  >
                    <span class="bk-sym" aria-hidden="true">download</span>
                    {{ exportBusy === "failed" ? "Preparing…" : "Download failed numbers" }}
                  </button>
                </div>
              </ng-container>
            </div>
          }
        </section>
      </div>

      <div class="md__col">
        <!-- Facts -->
        <section class="bk-panel md__card" style="--i: 2" aria-labelledby="md-facts">
          <h2 class="bk-eyebrow" id="md-facts">Details</h2>
          @if (loadState === "loading") {
            <div class="md__skel-stack" aria-hidden="true">
              @for (n of [0, 1, 2, 3, 4]; track n) {
                <span class="bk-skel" style="width: 100%; height: 14px"></span>
              }
            </div>
          } @else {
            <dl class="bk-kv md__kv">
              <dt>Status</dt>
              <dd>
                @if (status) {
                  <app-status [text]="status" [status]="status" />
                } @else {
                  —
                }
              </dd>

              <dt>Sender ID</dt>
              <dd>{{ from || "—" }}</dd>

              <dt>Type</dt>
              <dd>{{ typeLabel }}</dd>

              <dt>{{ momentLabel }}</dt>
              <dd>
                @if (moment) {
                  <time [attr.datetime]="momentIso || null">{{ momentExact || momentText }}</time>
                  @if (zoneTime) {
                    <span class="md__quiet"> your time</span>
                  }
                } @else {
                  —
                }
                @if (countdown) {
                  <span class="md__quiet"> · {{ countdown }}</span>
                }
              </dd>

              @if (scheduled && timeZone) {
                <dt>Time zone</dt>
                <dd>
                  {{ timeZone }}
                  @if (zoneTime) {
                    <span class="md__quiet"> · {{ zoneTime }} there</span>
                  }
                </dd>
              }

              <dt>Recipients</dt>
              <dd>
                @if (isLoadingRecipients) {
                  <span class="bk-skel md__kv-skel" role="status" aria-label="Loading recipients"></span>
                } @else if (recipientsError) {
                  <span class="md__error-text">{{ recipientsError }}</span>
                } @else {
                  {{ formatCount(totalRecipients) }}
                }
              </dd>

              @if (blacklistedCount > 0) {
                <dt>Left out</dt>
                <dd>{{ formatCount(blacklistedCount) }} blacklisted</dd>
              }

              <dt>Length</dt>
              <dd>
                {{ partsText }}
                @if (partsCount && partsEstimated) {
                  <span class="md__quiet"> · varies with personal fields</span>
                }
              </dd>

              @if (smsTotalText) {
                <dt>SMS in total</dt>
                <dd>{{ smsTotalText }}</dd>
              }

              @if (createdText) {
                <dt>Created</dt>
                <dd>
                  <time [attr.datetime]="createdIso || null">{{ createdExact || createdText }}</time>
                  @if (createdBy) {
                    <span class="md__quiet"> by {{ createdBy }}</span>
                  }
                </dd>
              }
            </dl>
          }
        </section>

        <!-- Audience -->
        <section class="bk-panel md__card" style="--i: 3" aria-labelledby="md-audience">
          <header class="md__card-head">
            <h2 class="bk-eyebrow" id="md-audience">Audience</h2>
          </header>

          @if (isLoadingRecipients) {
            <div class="md__skel-stack" aria-hidden="true">
              <span class="bk-skel" style="width: 96px; height: 28px"></span>
              <span class="bk-skel" style="width: 70%; height: 14px"></span>
            </div>
          } @else if (recipientsError) {
            <p class="md__note">{{ recipientsError }} Refresh the page to try again.</p>
          } @else {
            <p class="md__audience-total">
              <strong>{{ formatCount(totalRecipients) }}</strong>
              {{ totalRecipients === 1 ? "recipient" : "recipients" }}
            </p>
            @if (audience.length) {
              <ul class="md__audience">
                @for (group of audience; track group.label) {
                  <li>
                    <span>{{ group.label }}</span>
                    <span class="md__audience-count">{{ formatCount(group.count) }}</span>
                  </li>
                }
              </ul>
            }
            <p class="md__hint">
              The total counts everyone reached through tags, organizations,
              segments and typed-in numbers.
            </p>
          }

          <div>
            <button type="button" class="bk-btn bk-btn--ghost" (click)="handleTabMenu('View recipients')">
              <span class="bk-sym" aria-hidden="true">group</span>
              View recipients
            </button>
          </div>
        </section>
      </div>
    </div>
  }
</div>

<!-- MODALS -->
@if (open_recipient) {
  <recipient-list
    (close)="handleRecipientClose()"
    [detail_data]="recipient"
    [messageUuid]="messageUuid || getMessageDetails?.uuid"
    [add_recipient]="open_recipient"
    [recipientsData]="recipientsData"
  ></recipient-list>
}

<!-- Cancel a scheduled send -->
<app-prompt-card
  title="Cancel this scheduled send?"
  buttonText="Cancel send"
  closeText="Keep scheduled"
  [openModal]="showCancelPrompt"
  [loading]="cancelling"
  [impact]="cancelImpact"
  impactIcon="schedule_send"
  (onClose)="closeCancelPrompt()"
  (onButtonClick)="confirmCancel()"
>
  <div class="md__prompt">
    <p>
      <strong>{{ subject }}</strong> will not go out. To send it later, duplicate
      it and schedule the copy.
    </p>
    @if (cancelError) {
      <p class="md__error-text" role="alert">{{ cancelError }}</p>
    }
  </div>
</app-prompt-card>

<!-- Resend: only asked when the numbers cannot travel in the link -->
<app-prompt-card
  title="Resend to failed numbers"
  buttonText="Open new message"
  tone="default"
  [openModal]="!!resendPrompt"
  (onClose)="closeResendPrompt()"
  (onButtonClick)="confirmResend()"
>
  <div class="md__prompt">
    <p>
      {{ formatCount(resendCount) }} failed {{ resendCount === 1 ? "number goes" : "numbers go" }}
      into a new message with the subject filled in. Nothing is sent until you
      review it and send it.
    </p>
    @if (resendViaState) {
      <p>
        That is too many to carry in a link, so the numbers are handed to the new
        message directly. Reloading that page clears them: download the failed
        numbers first if you need a copy.
      </p>
    }
    @if (resendCapped) {
      <p>Only the first {{ formatCount(collectCap) }} failed rows are included.</p>
    }
    @if (resendBlacklisted) {
      <p>
        {{ formatCount(resendBlacklisted) }} blacklisted
        {{ resendBlacklisted === 1 ? "number is" : "numbers are" }} left out.
      </p>
    }
  </div>
</app-prompt-card>
